import React from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import EmptyResultSetMessage from 'components/@common/EmptyResultSetMessage';
import PageContainer from 'components/@layout/PageContainer';
import GET_MY_ACTIVE_ORDERS from 'graph/orders/get-my-orders-query';
import GET_UNAUTHENTICATED_ORDER from 'graph/orders/get-unauthenticated-order';
import useUiStore from 'store/ui';
import useUserStore from 'store/user';
import { Mastercode, Order } from 'types/schema-generated-types';
import OrdersList from './components/OrderList';
import RosteringInformationAnnouncement from './components/RosteringInformationAnnouncement';
import ActivateMastercodeModal from './modals/ActivateMastercodeModal';
import TransferMastercodeModal from './modals/TransferMastercodeModal';

export type Props = {
  orderId?: number | null;
  ownerEmail?: string | Array<string> | null;
};

type MastercodeActionProps = { order: Order; mastercode?: Mastercode };
export type MastercodeActionClickHandler = (props: MastercodeActionProps) => () => void;

const OrdersPage = ({ orderId, ownerEmail }: Props) => {
  const classes = useStyles();
  const history = useHistory();
  const [, { openQuarterModal, closeModal }] = useUiStore();
  const [{ me }] = useUserStore();

  const ordersQuery = orderId ? GET_UNAUTHENTICATED_ORDER : GET_MY_ACTIVE_ORDERS;
  const ordersVariable = orderId ? { orderId, ownerEmail } : { where: { completed: false } };
  const { data, loading, error, refetch } = useQuery(ordersQuery, {
    variables: ordersVariable,
    fetchPolicy: 'network-only'
  });

  const navigateToRosterManagement = React.useCallback(() => {
    history.push('/admin/roster-management');
  }, [history]);

  const onActivateMastercodeClick = React.useCallback<MastercodeActionClickHandler>(
    ({ order, mastercode }: MastercodeActionProps) => () => {
      if (orderId) {
        history.push('/dashboard/orders');
      } else {
        openQuarterModal({
          body: (
            <ActivateMastercodeModal
              order={order}
              onClose={closeModal}
              onSubmit={refetch}
              onRosterManagementClick={navigateToRosterManagement}
              {...(mastercode ? { mastercodeId: mastercode.id } : { activateAll: true })}
            />
          )
        });
      }
    },
    [orderId, history, openQuarterModal, navigateToRosterManagement, closeModal, refetch]
  );

  const onTransferMastercodeClick = React.useCallback<MastercodeActionClickHandler>(
    ({ order, mastercode }: MastercodeActionProps) => () => {
      openQuarterModal({
        body: (
          <TransferMastercodeModal
            order={order}
            ownerEmail={ownerEmail}
            onClose={closeModal}
            onSubmit={refetch}
            {...(mastercode ? { mastercodeId: mastercode.id } : { activateAll: true })}
          />
        )
      });
    },
    [ownerEmail, openQuarterModal, closeModal, refetch]
  );

  let orders: Order[] = [];
  if (data?.me) {
    orders = data.me.orders ?? [];
  } else if (data?.order) {
    orders = [data.order];
  }

  return (
    <PageContainer loading={loading} error={error}>
      <div className={classes.container}>
        {me && <RosteringInformationAnnouncement />}
        {orders.length === 0 ? (
          <EmptyResultSetMessage
            style={{ marginTop: '12px' }}
            message={'No Orders to Activate'}
            subtitle={'You have no new orders to activate or transfer.'}
          />
        ) : (
          <div className={classes.ordersContainer}>
            <Typography className={classes.ordersHeader} variant="h2">
              Orders
            </Typography>
            <OrdersList
              orders={orders}
              onActivateMastercodeClick={onActivateMastercodeClick}
              onTransferMastercodeClick={onTransferMastercodeClick}
            />
          </div>
        )}
      </div>
    </PageContainer>
  );
};

export default OrdersPage;

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    padding: theme.spacing(4)
  },
  ordersContainer: {
    marginTop: theme.spacing(4)
  },
  ordersHeader: {
    marginBottom: theme.spacing(1)
  }
}));
